import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from '../Link';
import { MyJobsNavEntry } from './MyJobsNavEntry';
import { Logo } from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { UserMenu } from './UserMenu';
import type { IntlShape } from 'react-intl';
import { useJobIslandContext } from '../../contexts/JobIslandContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useFeedbackModalOptional } from '../../contexts/FeedbackModalContext';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { trackEvent } from '../../utils/analytics/analytics';
import { CORE_HEADER_STICKY, CORE_NAV_LINK } from '../../constants/corePublicTheme';

export interface NavigationNavbarProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
}

/**
 * Navigation navbar component - shows on non-SRP pages
 * Contains logo, navigation links, tools menu, language selector, and user menu
 */
export const NavigationNavbar: React.FC<NavigationNavbarProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  clearSearch,
  user,
}) => {
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const pathname = jobIsland?.pathname ?? (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  // Prefer job island, then pageContext, then intl, then pathname so static and hydrated both show correct locale
  const rawLocale = jobIsland?.locale ?? pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en';
  const locale = normalizeSupportedLocale(rawLocale);
  const searchPageUrl = `/${locale}`;
  const resourcesPath = locale === 'fi' ? 'tyoelama' : locale === 'sv' ? 'arbetsliv' : 'resources';
  const locationsPath = locale === 'fi' ? 'sijainnit' : locale === 'sv' ? 'platser' : 'locations';

  const auth = useAuthOptional();
  const feedbackModal = useFeedbackModalOptional();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const showGuestNavEngagement =
    hasMounted &&
    auth &&
    !auth.isAuthenticated &&
    (feedbackModal != null || typeof auth.signIn === 'function');

  return (
    <header className={CORE_HEADER_STICKY} role="banner">
      <nav className="flex items-center gap-3 py-3 px-4 xl:px-20 md:px-10" aria-label="Main navigation">
        {/* Left side: Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Logo clearSearch={clearSearch} variant="horizontal" locale={locale} />
          <Logo clearSearch={clearSearch} variant="compact" locale={locale} />
        </div>

        {/* Center: same primary nav on every page — the tools-page-only ToolSwitcher branch is
            retired; the hub's own tab row (HubTabs) now lives inside the /tools page body. */}
        <nav className="hidden lg:flex items-center gap-4 flex-1 justify-center">
          <Link href="/" className={CORE_NAV_LINK}>
            <FormattedMessage id="navigation.home" defaultMessage="Home" description="Navigation link to home page" />
          </Link>
          <Link href={`/${locale}/${resourcesPath}`} className={CORE_NAV_LINK}>
            <FormattedMessage id="usermenu.blog.link" defaultMessage="Resources" description="Navigation link to resources/blog page" />
          </Link>
          <Link href={`/${locale}/${locationsPath}`} className={CORE_NAV_LINK}>
            <FormattedMessage id="navigation.locations" defaultMessage="Locations" description="Navigation link to locations hub page" />
          </Link>
          <Link href={searchPageUrl} className={CORE_NAV_LINK}>
            <FormattedMessage id="navigation.search" defaultMessage="Search Jobs" description="Navigation link to job search page" />
          </Link>
        </nav>

        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {showGuestNavEngagement ? (
            <div className="hidden md:flex items-center gap-2 mr-1">
              {feedbackModal ? (
                <button
                  type="button"
                  onClick={() =>
                    feedbackModal.openFeedback({
                      source: 'navigation_desktop',
                      metadata: { pathname },
                    })
                  }
                  className="text-sm font-medium text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] px-2 py-1 rounded-lg hover:bg-[#F2EADF]/80 dark:hover:bg-[#2A1D0F]/80 cursor-pointer"
                >
                  <FormattedMessage
                    id="engagement.nav.feedback"
                    defaultMessage="Feedback"
                    description="Desktop nav link to open feedback modal"
                  />
                </button>
              ) : null}
              {auth && !auth.isAuthenticated ? (
                <button
                  type="button"
                  onClick={() => {
                    trackEvent({ name: 'auth_interaction', params: { action: 'sign_in_click', surface: 'navbar', locale } });
                    auth.signIn();
                  }}
                  className="text-sm font-semibold text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] px-3 py-1.5 rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/90 dark:bg-[#1F1509]/90 hover:bg-[#F2EADF] dark:hover:bg-[#342415] cursor-pointer"
                >
                  <FormattedMessage
                    id="engagement.nav.signIn"
                    defaultMessage="Sign in"
                    description="Desktop nav sign-in button for guests"
                  />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="hidden sm:block">
            <MyJobsNavEntry locale={locale} />
          </div>
          <LanguageSelector intl={intl} locale={locale} />
          <UserMenu
            intl={intl}
            user={user}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        </div>
      </nav>
    </header>
  );
};
